import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ExternalLink, Loader2, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { initPixel, trackEvent } from "@/lib/pixel";

export default function ThankYou() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [supportEmail, setSupportEmail] = useState("support@floxora.in");

  useEffect(() => {
    if (!orderId) {
      setState({ loading: false, data: null, error: "Order ID is missing" });
      return;
    }
    api.get("/config").then((r) => {
      initPixel(r.data.meta_pixel_id);
      if (r.data.support_email) setSupportEmail(r.data.support_email);
    }).catch(() => {});
    api.get(`/payments/status/${orderId}`)
      .then((r) => {
        setState({ loading: false, data: r.data, error: null });
        if (r.data.status === "paid") {
          trackEvent("Purchase", { value: r.data.amount, currency: "INR" });
        }
      })
      .catch(() => setState({ loading: false, data: null, error: "Order not found" }));
  }, [orderId]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="thankyou-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-lg bg-[#0b0b10] border border-white/10 rounded-3xl p-10 sm:p-14 text-center"
      >
        {state.loading ? (
          <div className="flex flex-col items-center gap-4" data-testid="thankyou-loading">
            <Loader2 className="w-10 h-10 text-[#A78BFA] animate-spin" />
            <p className="text-zinc-400">Payment confirm ho raha hai...</p>
          </div>
        ) : state.error ? (
          <div data-testid="thankyou-error">
            <h1 className="font-['Outfit'] font-bold text-2xl mb-3">Oops!</h1>
            <p className="text-zinc-400 text-sm mb-6">{state.error}</p>
            <Link to="/" className="text-[#A78BFA] underline text-sm" data-testid="back-home-link">Back to home</Link>
          </div>
        ) : state.data?.status === "paid" ? (
          <div data-testid="thankyou-success">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.15 }}>
              <CheckCircle2 className="w-16 h-16 text-[#A78BFA] mx-auto" />
            </motion.div>
            <h1 className="font-['Outfit'] font-extrabold tracking-tighter text-4xl mt-6">Payment Successful!</h1>
            <p className="text-zinc-400 text-sm mt-3">
              Thank you {state.data.name}! Your <span className="text-white font-medium">{state.data.product_name}</span> is ready.
            </p>
            {state.data.drive_link ? (
              <a
                href={state.data.drive_link} target="_blank" rel="noopener noreferrer"
                onClick={() => api.post("/track-open", { order_id: orderId }).catch(() => {})}
                data-testid="drive-link-button"
                className="gold-glow mt-8 inline-flex items-center gap-2 rounded-full bg-[#A78BFA] text-black font-extrabold px-8 py-4 hover:bg-[#fbbf24] transition-colors active:scale-95"
              >
                <ExternalLink className="w-5 h-5" /> Access Your Bundle (Google Drive)
              </a>
            ) : (
              <p className="mt-8 text-sm text-zinc-400 flex items-center justify-center gap-2 flex-wrap" data-testid="email-fallback-note">
                <Mail className="w-4 h-4 text-[#A78BFA]" /> Your access link will be shared shortly. Contact <a href={`mailto:${supportEmail}`} className="text-[#A78BFA] underline">{supportEmail}</a>
              </p>
            )}
            <p className="mt-6 text-xs text-zinc-400">Order ID: {orderId}</p>
            <p className="mt-1 text-xs text-zinc-400">Save this page for your records.</p>
          </div>
        ) : (
          <div data-testid="thankyou-pending">
            <h1 className="font-['Outfit'] font-bold text-2xl mb-3">Payment Pending</h1>
            <p className="text-zinc-400 text-sm">Your payment is not confirmed yet. If the amount was deducted, please refresh this page in a few minutes.</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
